import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Stack,
  Divider,
  Avatar,
  LinearProgress,
  Pagination,
  Autocomplete,
  Badge,
} from '@mui/material';
import {
  Book as BookIcon,
  Keyboard as KeyboardIcon,
  Speed as SpeedIcon,
  Assignment as AssignmentIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  DateRange as DateRangeIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  Timeline as TimelineIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBaseUrl, getAuthHeader } from '../../services/httpClient';

const API_BASE_URL = getApiBaseUrl();

const AdminTrackSheetsDashboard = () => {
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState('all'); // 'all', 'daily', 'student'
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [activityType, setActivityType] = useState('all');
  const [courseFilter, setCourseFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const limit = 20;

  // Fetch students list for autocomplete
  const { data: studentsData } = useQuery(
    'students-list',
    async () => {
      const response = await axios.get(`${API_BASE_URL}/students?limit=1000`, {
        headers: getAuthHeader(),
      });
      return response.data;
    },
    { enabled: true }
  );

  const students = studentsData?.students || [];

  // Fetch courses list
  const { data: coursesData } = useQuery(
    'courses-list',
    async () => {
      const response = await axios.get(`${API_BASE_URL}/students/courses/list`);
      return response.data;
    }
  );

  const courses = coursesData?.courses || [];
  const courseMap = React.useMemo(() => {
    const map = {};
    courses.forEach((course) => {
      map[course.course_code] = course.course_name;
    });
    return map;
  }, [courses]);

  // Build query params based on view mode
  const getQueryParams = () => {
    const params = {
      page,
      limit,
    };

    if (viewMode === 'daily') {
      params.date = selectedDate;
    } else if (viewMode === 'student' && selectedStudent) {
      params.studentId = selectedStudent.id;
    } else {
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
    }

    if (activityType !== 'all') params.activityType = activityType;
    if (courseFilter !== 'all') params.course = courseFilter;
    if (statusFilter !== 'all') params.status = statusFilter;
    if (searchQuery) params.search = searchQuery;

    return params;
  };

  // Fetch track sheets
  const { data, isLoading, error, refetch } = useQuery(
    ['admin-track-sheets', viewMode, selectedDate, selectedStudent, startDate, endDate, activityType, courseFilter, statusFilter, searchQuery, page],
    async () => {
      const params = getQueryParams();
      const response = await axios.get(`${API_BASE_URL}/track-sheets/admin/all`, {
        params,
        headers: getAuthHeader(),
      });
      return response.data;
    },
    { keepPreviousData: true }
  );

  // Fetch daily summary
  const { data: dailySummary } = useQuery(
    ['daily-summary', selectedDate],
    async () => {
      const response = await axios.get(`${API_BASE_URL}/track-sheets/admin/daily-summary`, {
        params: { date: selectedDate },
        headers: getAuthHeader(),
      });
      return response.data;
    },
    { enabled: viewMode === 'daily' }
  );

  const trackSheets = data?.trackSheets || [];
  const pagination = data?.pagination || { total: 0, totalPages: 0 };

  const loadSheetDetails = async (date, studentId) => {
    try {
      setLoadingDetails(true);
      const response = await axios.get(`${API_BASE_URL}/track-sheets/${date}?studentId=${studentId}`, {
        headers: getAuthHeader(),
      });

      if (response.data.success && response.data.trackSheet) {
        setSelectedSheet(response.data.trackSheet);
        setDetailsOpen(true);
      }
    } catch (error) {
      console.error('Error loading sheet details:', error);
      toast.error('Failed to load track sheet details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-IN', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  const getCourseNames = (courses) => {
    if (!courses || !Array.isArray(courses)) return 'N/A';
    return courses.map((c) => courseMap[c] || c).join(', ') || 'N/A';
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'success',
      completed: 'success',
      inactive: 'warning',
      deleted: 'error',
    };
    return colors[status] || 'default';
  };

  const formatDuration = (seconds) => {
    const total = Number(seconds) || 0;
    if (total <= 0) return '0m';
    const hours = Math.floor(total / 3600);
    const minutes = Math.round((total % 3600) / 60);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setActivityType('all');
    setCourseFilter('all');
    setStatusFilter('all');
    setSearchQuery('');
    setSelectedStudent(null);
    setPage(1);
  };

  const hasActiveFilters = startDate || endDate || activityType !== 'all' || courseFilter !== 'all' || statusFilter !== 'all' || searchQuery || selectedStudent;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Track Sheets Dashboard
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Monitor all student activities and track their progress
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Refresh">
                <IconButton onClick={() => refetch()} sx={{ color: 'white' }}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              {hasActiveFilters && (
                <Tooltip title="Clear Filters">
                  <IconButton onClick={handleClearFilters} sx={{ color: 'white' }}>
                    <ClearIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {/* View Mode Tabs */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Tabs value={viewMode} onChange={(e, v) => { setViewMode(v); setPage(1); }} sx={{ mb: 3 }}>
            <Tab label="All Activities" value="all" icon={<TimelineIcon />} iconPosition="start" />
            <Tab label="Daily View" value="daily" icon={<CalendarIcon />} iconPosition="start" />
            <Tab label="Student View" value="student" icon={<PersonIcon />} iconPosition="start" />
          </Tabs>

          {/* Daily Summary */}
          {viewMode === 'daily' && dailySummary?.summary && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined" sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                  <CardContent>
                    <Typography variant="caption">Active Students</Typography>
                    <Typography variant="h4">{dailySummary.summary.active_students || 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined" sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>
                  <CardContent>
                    <Typography variant="caption">Total Lessons</Typography>
                    <Typography variant="h4">{dailySummary.summary.total_lessons || 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined" sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
                  <CardContent>
                    <Typography variant="caption">Speed Practices</Typography>
                    <Typography variant="h4">{dailySummary.summary.total_speed_practices || 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined" sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                  <CardContent>
                    <Typography variant="caption">Exams</Typography>
                    <Typography variant="h4">{dailySummary.summary.total_exams || 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Filters */}
          <Grid container spacing={2}>
            {viewMode === 'daily' && (
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Select Date"
                  type="date"
                  value={selectedDate}
                  onChange={(e) => { setSelectedDate(e.target.value); setPage(1); }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}

            {viewMode === 'student' && (
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={students}
                  getOptionLabel={(option) => `${option.name} (${option.phone})`}
                  value={selectedStudent}
                  onChange={(e, newValue) => { setSelectedStudent(newValue); setPage(1); }}
                  renderInput={(params) => <TextField {...params} label="Select Student" />}
                />
              </Grid>
            )}

            {viewMode === 'all' && (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={startDate}
                    onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    value={endDate}
                    onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} sm={6} md={viewMode === 'all' ? 2 : 3}>
              <FormControl fullWidth>
                <InputLabel>Activity Type</InputLabel>
                <Select
                  value={activityType}
                  label="Activity Type"
                  onChange={(e) => { setActivityType(e.target.value); setPage(1); }}
                >
                  <MenuItem value="all">All Activities</MenuItem>
                  <MenuItem value="notes">Notes / Reading</MenuItem>
                  <MenuItem value="video">Videos</MenuItem>
                  <MenuItem value="flashcards">Flashcards</MenuItem>
                  <MenuItem value="quizzes">Quizzes</MenuItem>
                  <MenuItem value="lessons">Legacy Lessons</MenuItem>
                  <MenuItem value="shortcuts">Shortcuts</MenuItem>
                  <MenuItem value="speed">Speed Practice</MenuItem>
                  <MenuItem value="exams">Legacy Exams</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={viewMode === 'all' ? 2 : 3}>
              <FormControl fullWidth>
                <InputLabel>Course</InputLabel>
                <Select
                  value={courseFilter}
                  label="Course"
                  onChange={(e) => { setCourseFilter(e.target.value); setPage(1); }}
                >
                  <MenuItem value="all">All Courses</MenuItem>
                  {courses.map((course) => (
                    <MenuItem key={course.course_code} value={course.course_code}>
                      {course.course_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={viewMode === 'all' ? 2 : 3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={viewMode === 'all' ? 2 : 3}>
              <TextField
                fullWidth
                label="Search Student"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                placeholder="Name or Phone"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      {viewMode === 'all' && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Total Track Sheets
                    </Typography>
                    <Typography variant="h5">{pagination.total || 0}</Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'primary.light' }}>
                    <AssessmentIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Active Students
                    </Typography>
                    <Typography variant="h5">
                      {new Set(trackSheets.map((ts) => ts.student_id)).size}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'success.light' }}>
                    <PersonIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Notes / Reading
                    </Typography>
                    <Typography variant="h5">
                      {trackSheets.reduce((sum, ts) => sum + (parseInt(ts.notes_count ?? ts.lesson_count) || 0), 0)}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'info.light' }}>
                    <BookIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Videos
                    </Typography>
                    <Typography variant="h5">
                      {trackSheets.reduce((sum, ts) => sum + (parseInt(ts.video_count) || 0), 0)}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'secondary.light' }}>
                    <TimelineIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Flashcards
                    </Typography>
                    <Typography variant="h5">
                      {trackSheets.reduce((sum, ts) => sum + (parseInt(ts.flashcard_count) || 0), 0)}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'info.light' }}>
                    <SchoolIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Quizzes
                    </Typography>
                    <Typography variant="h5">
                      {trackSheets.reduce((sum, ts) => sum + (parseInt(ts.quiz_count ?? ts.exam_count) || 0), 0)}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'warning.light' }}>
                    <AssignmentIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Track Sheets Table */}
      <Card>
        <CardContent>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">Failed to load track sheets. Please try again.</Alert>
          ) : trackSheets.length === 0 ? (
            <Alert severity="info">No track sheets found matching your filters.</Alert>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'primary.main' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Student</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Phone</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Courses</TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>
                        <BookIcon sx={{ fontSize: 18, mr: 0.5 }} />
                        Notes
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>
                        <TimelineIcon sx={{ fontSize: 18, mr: 0.5 }} />
                        Video
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>
                        <SchoolIcon sx={{ fontSize: 18, mr: 0.5 }} />
                        Flashcards
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>
                        <AssignmentIcon sx={{ fontSize: 18, mr: 0.5 }} />
                        Quizzes
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Time</TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trackSheets.map((sheet) => (
                      <TableRow key={sheet.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {formatDate(sheet.track_date)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {sheet.track_date}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {sheet.student_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {sheet.student_code}
                          </Typography>
                        </TableCell>
                        <TableCell>{sheet.student_phone}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                            {getCourseNames(sheet.enrolled_courses)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            color="primary"
                            label={sheet.notes_count ?? sheet.lesson_count ?? 0}
                            variant={parseInt(sheet.notes_count ?? sheet.lesson_count) > 0 ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={sheet.video_count || 0}
                            size="small"
                            color="secondary"
                            variant={parseInt(sheet.video_count) > 0 ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={sheet.flashcard_count || 0}
                            size="small"
                            color="info"
                            variant={parseInt(sheet.flashcard_count) > 0 ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={sheet.quiz_count ?? sheet.exam_count ?? 0}
                            size="small"
                            color="success"
                            variant={parseInt(sheet.quiz_count ?? sheet.exam_count) > 0 ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="medium">
                            {formatDuration(sheet.total_time_seconds)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={sheet.student_status}
                            size="small"
                            color={getStatusColor(sheet.student_status)}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => loadSheetDetails(sheet.track_date, sheet.student_id)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={pagination.totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                    size="large"
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              Track Sheet Details - {selectedSheet && formatDate(selectedSheet.track_date)}
            </Typography>
            <IconButton onClick={() => setDetailsOpen(false)} size="small">
              <ClearIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingDetails ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedSheet ? (
            <Box>
              {/* Lesson Details */}
              {selectedSheet.lessonDetails && selectedSheet.lessonDetails.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <BookIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Lesson Details ({selectedSheet.lessonDetails.length})
                    </Typography>
                  </Box>
                  {selectedSheet.lessonDetails.map((lesson, index) => (
                    <Card key={index} variant="outlined" sx={{ mb: 2, ml: 4 }}>
                      <CardContent>
                        <Typography variant="body1" fontWeight="medium" gutterBottom>
                          {lesson.lesson_name}
                        </Typography>
                        {lesson.points && lesson.points.length > 0 && (
                          <List dense>
                            {lesson.points.map((point, pIndex) => (
                              <ListItem key={pIndex} sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 24 }}>
                                  <Typography variant="body2" color="primary">•</Typography>
                                </ListItemIcon>
                                <ListItemText
                                  primary={point.point_text}
                                  primaryTypographyProps={{ variant: 'body2' }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}

              {selectedSheet.lmsActivities && selectedSheet.lmsActivities.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TimelineIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      LMS App Activity ({selectedSheet.lmsActivities.length})
                    </Typography>
                  </Box>
                  <Grid container spacing={1}>
                    {selectedSheet.lmsActivities.map((activity, index) => (
                      <Grid item xs={12} md={6} key={`${activity.content_id}-${index}`}>
                        <Card variant="outlined">
                          <CardContent sx={{ py: 1.5 }}>
                            <Stack direction="row" justifyContent="space-between" spacing={1}>
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {activity.title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {activity.course_code} • {activity.type} • {formatDuration(activity.time_spent_seconds)}
                                </Typography>
                              </Box>
                              <Chip
                                size="small"
                                color={activity.is_completed ? 'success' : 'warning'}
                                label={activity.is_completed ? 'Completed' : 'Accessed'}
                              />
                            </Stack>
                            {activity.type === 'quiz' && activity.score != null && (
                              <Typography variant="caption" color="primary">
                                Score: {activity.score}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Shortcuts */}
              {selectedSheet.shortcuts && selectedSheet.shortcuts.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <KeyboardIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Shortcuts ({selectedSheet.shortcuts.length})
                    </Typography>
                  </Box>
                  <Grid container spacing={1}>
                    {selectedSheet.shortcuts.map((shortcut, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Card variant="outlined">
                          <CardContent sx={{ py: 1.5 }}>
                            <Typography variant="body2" fontWeight="medium">
                              {shortcut.shortcut_key}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {shortcut.use_case}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Speed Practice */}
              {selectedSheet.speedPractice && selectedSheet.speedPractice.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SpeedIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Speed Practice ({selectedSheet.speedPractice.length})
                    </Typography>
                  </Box>
                  <Grid container spacing={1}>
                    {selectedSheet.speedPractice.map((practice, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Card variant="outlined">
                          <CardContent sx={{ py: 1.5 }}>
                            <Typography variant="body2" fontWeight="medium">
                              {practice.practice_type} - {practice.lesson_name}
                            </Typography>
                            <Typography variant="h6" color="primary">
                              {practice.speed} WPM
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Exam Results */}
              {selectedSheet.examResults && selectedSheet.examResults.length > 0 && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AssignmentIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Exam Results ({selectedSheet.examResults.length})
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    {selectedSheet.examResults.map((exam, index) => {
                      const percentage = (exam.obtained_marks / exam.total_marks) * 100;
                      return (
                        <Grid item xs={12} sm={6} key={index}>
                          <Card variant="outlined">
                            <CardContent>
                              <Typography variant="body1" fontWeight="medium" gutterBottom>
                                {exam.exam_name}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                                <Typography variant="h5" color="primary">
                                  {exam.obtained_marks}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  / {exam.total_marks}
                                </Typography>
                                <Chip
                                  label={`${percentage.toFixed(1)}%`}
                                  size="small"
                                  color={percentage >= 80 ? 'success' : percentage >= 60 ? 'warning' : 'error'}
                                />
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={percentage}
                                color={percentage >= 80 ? 'success' : percentage >= 60 ? 'warning' : 'error'}
                                sx={{ height: 8, borderRadius: 4 }}
                              />
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )}

              {!selectedSheet.lessonDetails?.length &&
                !selectedSheet.shortcuts?.length &&
                !selectedSheet.speedPractice?.length &&
                !selectedSheet.examResults?.length && (
                  <Alert severity="info">No data recorded for this date</Alert>
                )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminTrackSheetsDashboard;














